import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, XCircle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAccountHealth } from "@/hooks/use-account-health";
import { useBrandContext } from "@/contexts/brand-context";

export function AccountHealthWidget() {
  const { t } = useTranslation("dashboard");
  const { brand } = useBrandContext();
  const { data, isLoading } = useAccountHealth(brand?.id ?? "");

  if (!brand) return null;
  if (isLoading || !data) return null;
  if (data.summary.total === 0) return null;

  const { summary } = data;
  const allHealthy = summary.healthy === summary.total;
  const hasError = summary.error > 0;
  const Icon = allHealthy ? CheckCircle2 : hasError ? XCircle : AlertTriangle;
  const tone = allHealthy
    ? "text-emerald-600"
    : hasError
      ? "text-rose-600"
      : "text-amber-600";

  const headline = allHealthy
    ? t("accountHealth.allHealthy", { count: summary.total })
    : t("accountHealth.needsAttention", { unhealthy: summary.total - summary.healthy, total: summary.total });

  // Surface the worst account's recommendation, if any.
  const worst =
    data.accounts.find((a) => a.status === "error" && a.recommendation) ??
    data.accounts.find((a) => a.status === "warning" && a.recommendation);

  // Stale-check: if the most recent check is >2h old, hint about it.
  const lastChecked = data.accounts
    .map((a) => (a.checkedAt ? new Date(a.checkedAt).getTime() : 0))
    .reduce((m, n) => Math.max(m, n), 0);
  const stale = lastChecked > 0 && Date.now() - lastChecked > 2 * 60 * 60 * 1000;
  const neverChecked = lastChecked === 0;

  return (
    <Link
      to={`/${brand.slug}/settings/accounts`}
      className="block rounded-lg border bg-card p-4 transition hover:bg-muted/40"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{t("accountHealth.title")}</h3>
        <span className="text-xs text-muted-foreground">{t("accountHealth.manage")}</span>
      </div>
      <div className={`mt-2 flex items-center gap-2 text-base font-medium ${tone}`}>
        {neverChecked ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <Icon className="h-4 w-4" />
        )}
        <span>{neverChecked ? t("accountHealth.checking") : headline}</span>
      </div>
      {worst?.recommendation && !neverChecked && (
        <div className="mt-1 truncate text-xs text-muted-foreground" title={worst.recommendation}>
          {worst.recommendation}
        </div>
      )}
      {stale && !neverChecked && (
        <div className="mt-1 text-[11px] text-muted-foreground">
          {t("accountHealth.staleCheck")}
        </div>
      )}
    </Link>
  );
}
